// RDQ_ORDER — the first entry in the Proxy ACE workflow registry. Everything
// here is specific to reorder-quantity recalculation; the generic Intake
// Queue and item-detail panel never import from this file directly, only
// through PROXY_WORKFLOW_REGISTRY. All the actual math/decisioning is pure,
// unit-tested logic imported from utils/ — this module's job is to wire that
// logic to the registry's task/panel contract, not to reimplement it.
import type { ProxyJob } from '../mockProxyJobs';
import type { ProxyInboxEmail } from '../mockProxyEmails';
import { classifyEmail } from '../../utils/proxyEmailClassifier';
import { resolveProxyJob } from '../../utils/proxyQueue';
import { calculateRdq, type RdqInput } from '../../utils/rdqCalculator';
import { resolveOutreachContact, contactNote } from '../proxyFallbackSolicitors';
import type {
  ProxyTask, ProxyTaskSource, ProxyWorkflowContext, ProxyWorkflowDefinition,
  ProxyExtractedField, ProxyReviewPanelSchema, ProxyPanelContext,
} from '../proxyWorkflowTypes';

// Fixed outbound target for the print-instruction draft — Proxy has no
// printer-contact registry of its own yet (Regulatory's is a separate,
// section-scoped concern per the agent-first "separate instances" rule).
const PRINTER_EMAIL = 'print@fulfillmentcenter.com';

// A job's RDQ_ORDER task is done once ACE has documented/sent a recalculated
// RDQ for it — the same followUpHistory the outbound action itself writes.
// Done once the RDQ asked for in this particular email has been logged.
function rdqOrderResolved(job: ProxyJob, email: ProxyInboxEmail): boolean {
  return job.followUpHistory.some(r => (r.type === 'rdq_documented' || r.type === 'rdq_sent') && r.sourceEmailId === email.id);
}

// Single source of truth for a task's copy — used both when building the
// queue in bulk and when ACE opens a single job's RDQ_ORDER workflow directly
// (e.g. from free-text chat), so the strings never drift between the two.
export function buildRdqOrderTask(job: ProxyJob, source: ProxyTaskSource, email?: ProxyInboxEmail, createdAt?: string): ProxyTask {
  // Email-sourced: Discovery's revised RDQ request (ICT-4). `job_signal` (no
  // email at all) is reserved for Ace opening this workflow directly off a
  // free-text chat request.
  const reason = email
    ? 'Discovery asked for a revised RDQ. Holder counts changed since the original order.'
    : 'Partial material on file. Holder counts may have changed since the original order.';
  // Email-sourced: the email's own hand-set confidence (how explicit the
  // request was), else the classifier's flat rate.
  const confidence = email
    ? email.aceConfidence ?? Math.round(classifyEmail(email).confidence * 100)
    : (job.solicitor ? 92 : 65);
  return {
    id: email ? `rdq-email-${email.id}-${job.jobId}` : `rdq-signal-${job.jobId}`,
    workflowType: 'RDQ_ORDER',
    jobId: job.jobId,
    title: `${job.issuer} - RDQ recalculation`,
    subtitle: `Job ${job.jobId}`,
    reason,
    source,
    sourceEmailId: email?.id,
    sourceLabel: source === 'job_signal' ? 'job signal' : undefined,
    sourceDetail: source === 'job_signal' ? {
      rows: [
        { label: 'Material status', value: job.materialStatus },
        { label: 'Band', value: `${resolveProxyJob(job, null).band} · ${job.daysToVoteCutoff}d to vote cutoff` },
        { label: 'Outstanding', value: job.outstandingMaterialTypes.join(', ') || 'None' },
      ],
    } : undefined,
    createdAt: createdAt || job.voteCutoffDate,
    confidence,
    revisedFullSets: email?.revisedFullSets,
    revisedNaa: email?.revisedNaa,
  };
}

function buildTasks({ jobs, emails }: ProxyWorkflowContext): ProxyTask[] {
  const tasks: ProxyTask[] = [];

  // ICT-4 / ICT-6: Discovery emails when a job needs a new or revised RDQ.
  for (const email of emails) {
    if (!email.relatedJobId) continue;
    if (classifyEmail(email).category !== 'REVISED_RDQ') continue;
    const job = jobs.find(j => j.jobId === email.relatedJobId);
    if (!job || rdqOrderResolved(job, email)) continue;
    tasks.push(buildRdqOrderTask(job, 'email', email, email.date));
  }

  return tasks;
}

function defaultEdits(task: ProxyTask, job: ProxyJob): RdqInput {
  return {
    country: job.country,
    noElec: false,
    mailingAsNaa: job.country === 'CA',
    // Email-sourced tasks seed from the solicitor's revised request when Ace
    // extracted one; otherwise (job-signal, or no revision stated) seed from
    // the job's own on-file quantities — which is also the baseline "order
    // on file" compares against, so an un-revised task honestly shows "No
    // change" rather than a fabricated delta.
    activeFullSets: task.revisedFullSets ?? job.fullSetsNeeded,
    activeNaa: task.revisedNaa ?? job.naaNeeded,
    activeElectronic: 0,
  };
}

function buildPanel({ job, edits }: ProxyPanelContext): ProxyReviewPanelSchema {
  const calc = edits as RdqInput;
  // The pure calculation module (utils/rdqCalculator.ts, unit-tested in
  // rdqCalculator.test.ts) does all the actual RDQ math — this workflow only
  // shapes its output into the panel schema.
  const result = calculateRdq(calc);
  const onFile = calculateRdq({ ...calc, activeFullSets: job.fullSetsNeeded, activeNaa: job.naaNeeded, activeElectronic: 0 });
  const delta = result.totalSets - onFile.totalSets;
  const recipient = resolveOutreachContact(job);
  const naaLine = result.lines.find(l => l.materialType === 'NAA');
  const totalLabel = naaLine ? `${result.totalSets.toLocaleString()} sets (Full Set + NAA)` : `${result.totalSets.toLocaleString()} full sets`;

  const extractedFields: ProxyExtractedField[] = [
    { key: 'issuer', label: 'Issuer', display: job.issuer },
    { key: 'jobId', label: 'Job #', display: job.jobId },
    { key: 'cusip', label: 'CUSIP', display: job.cusip },
    {
      key: 'country', label: 'Country',
      display: `${calc.country} · ${calc.country === 'US' || !calc.mailingAsNaa ? 'full sets only' : 'full sets + NAA'}`,
      editable: { kind: 'enum', value: calc.country, options: [{ value: 'US', label: 'US' }, { value: 'CA', label: 'CA' }] },
    },
    {
      key: 'noElec', label: 'No Elec?', display: calc.noElec ? 'Yes' : 'No',
      editable: { kind: 'enum', value: calc.noElec, options: [{ value: false, label: 'No' }, { value: true, label: 'Yes' }] },
    },
    {
      key: 'mailingAsNaa', label: 'Mailing as NAA?', display: calc.country === 'CA' && calc.mailingAsNaa ? 'Yes' : 'No',
      editable: calc.country === 'CA'
        ? { kind: 'enum', value: calc.mailingAsNaa, options: [{ value: false, label: 'No' }, { value: true, label: 'Yes' }] }
        : undefined,
    },
    { key: 'activeFullSets', label: 'Active Full Set', display: calc.activeFullSets.toLocaleString(), editable: { kind: 'number', value: calc.activeFullSets } },
    { key: 'activeElectronic', label: 'Active Electronic', display: calc.activeElectronic.toLocaleString(), editable: { kind: 'number', value: calc.activeElectronic } },
    { key: 'testRow', label: 'Test row', display: 'ignored', strike: true },
  ];

  const orderSubject = `Revised RDQ - ${job.issuer} (Job ${job.jobId})`;
  const orderBody = delta !== 0
    ? `Please process a revised record-date order of ${result.totalSets.toLocaleString()} full sets for the above job (up from ${onFile.totalSets.toLocaleString()}). CUSIP ${job.cusip}. Kindly confirm receipt and expected mail date.`
    : `Please process the record-date order of ${result.totalSets.toLocaleString()} full sets for the above job. CUSIP ${job.cusip}. Kindly confirm receipt and expected mail date.`;
  const printBody = `Job ${job.jobId}, ${job.issuer}. Print ${result.totalSets.toLocaleString()} full sets.${delta !== 0 ? ` Revised from ${onFile.totalSets.toLocaleString()} (${delta > 0 ? '+' : ''}${delta}).` : ''} Await confirmation before release.`;
  const micBody = `${totalLabel} (${delta === 0 ? 'no change' : `${delta > 0 ? '+' : ''}${delta} vs on file`})`;

  return {
    extractedFields,
    calculation: {
      title: 'RDQ calculation',
      ruleText: `${result.notes.join(' ')} Test-row quantities excluded.`,
      rows: [
        { label: 'Full sets to order', value: result.lines.find(l => l.materialType === 'Full Set')?.qtyToOrder.toLocaleString() ?? 'None' },
        { label: 'NAA to order', value: naaLine ? naaLine.qtyToOrder.toLocaleString() : 'None' },
      ],
      totalRow: { label: 'Order amount', value: totalLabel },
      deltaRow: {
        label: `Change vs order on file (${onFile.totalSets.toLocaleString()})`,
        value: delta === 0 ? 'No change' : `${delta > 0 ? '+' : ''}${delta}`,
        tone: delta === 0 ? 'muted' : 'positive',
      },
    },
    drafts: [
      ...(recipient ? [{
        key: 'order', icon: '📧', label: `Order email → broker (${recipient.name})`,
        to: recipient.email, cc: ['proxymaterial@betanxt.com'],
        note: contactNote(recipient),
        subject: orderSubject, body: orderBody,
      }] : []),
      {
        key: 'print', icon: '🖨️', label: 'Print instruction → printer',
        to: PRINTER_EMAIL, body: printBody,
      },
    ],
    approveSummary: [
      recipient ? 'Send both emails' : 'Send the print instruction (there\'s no solicitor contact on file, so contact tracing is needed before the order can go out; see Exceptions)',
      'Write the revised RDQ to the MIC Materials tab',
      'Log the action on the ticket',
      'Move this task to Done.',
    ],
    approveConfirmation: recipient
      ? `RDQ order sent to <strong>${recipient.name}</strong>, print instruction sent, and the revised RDQ documented in MIC for Job ${job.jobId}.`
      : `Print instruction sent and the revised RDQ documented in MIC for Job ${job.jobId}. There's no solicitor contact on file, so the order wasn't sent.`,
    closesTicket: true,
    outboundActions: [
      ...(recipient ? [{ key: 'order', followUpType: 'rdq_sent', recipientEmail: recipient.email, ccEmails: ['proxymaterial@betanxt.com'], subject: orderSubject, bodyPreview: orderBody }] : []),
      { key: 'print', followUpType: 'rdq_print', recipientEmail: PRINTER_EMAIL, subject: `Print instruction for Job ${job.jobId}`, bodyPreview: printBody },
      { key: 'mic', followUpType: 'rdq_documented', recipientEmail: 'mic@betanxt.com', subject: `RDQ documented for Job ${job.jobId}`, bodyPreview: micBody },
    ],
  };
}

// The approved order becomes the job's order on file, so later follow-ups
// chase the revised quantities.
function onApprove({ edits }: ProxyPanelContext): Partial<ProxyJob> {
  const result = calculateRdq(edits as RdqInput);
  const qty = (type: 'Full Set' | 'NAA') => result.lines.find(l => l.materialType === type)?.qtyToOrder ?? 0;
  return { fullSetsNeeded: qty('Full Set'), naaNeeded: qty('NAA') };
}

export const RDQ_ORDER_WORKFLOW: ProxyWorkflowDefinition = {
  type: 'RDQ_ORDER',
  label: 'RDQ Order',
  noun: 'an RDQ order',
  trigger: 'Discovery emails the ICT inbox that a job needs a new or revised RDQ (ICT-4, ICT-6).',
  source: 'Inbound email classified REVISED_RDQ.',
  steps: ['Source & extraction', 'RDQ calculation', 'Draft & approve'],
  buildTasks,
  defaultEdits,
  buildPanel,
  onApprove,
};
